/**
 * THE CLIENT HALF OF THE ATTENDANCE CONCERN SCAN.
 *
 * One POST to netlify/functions/attendanceConcerns, through a module-level
 * cache every consumer shares. Before this, the scan ran in the browser as a
 * live listener over a 120 day studio-wide window on `attendance`, which on
 * Bobbie's phone (2026-09-15: "Loading super slowly or not at all") meant
 * thousands of documents written into IndexedDB before the page could draw a
 * list of a dozen names.
 *
 * WHY A MODULE-LEVEL CACHE AND NOT A HOOK-LEVEL ONE. The staff home banner,
 * /attendance/concerns, the Command Center and the production tracker's comp
 * section all ask the same question, and a Director walks through two or three
 * of them in a minute. A per-hook fetch would pay for the whole scan each
 * time. The cache holds the in-flight promise as well as the settled answer,
 * so two surfaces mounting in the same tick share ONE call rather than racing
 * two.
 *
 * WHY A TTL AND NOT A SESSION CACHE. Attendance gets taken during class, and
 * two minutes is short enough that a Director who marks a register and walks
 * back to this list sees the new answer without thinking about it, while a
 * page she leaves open all evening still costs one scan per two minutes at
 * worst rather than one per render. Anything she wants sooner than that is
 * what Refresh is for, and Refresh bypasses this entirely.
 *
 * NO STATIC FIREBASE IMPORT. firebase/auth and ./functionSecurity load
 * dynamically, the same discipline catalogEditsClient.ts records: a render
 * harness that mocks ./firebase with a bare `db` and no App Check instance
 * would otherwise fail at module-graph load time and blank the page, instead
 * of failing as a rejected promise inside an already-caught call.
 */
// Pure `import type`, so this one IS erased and would resolve either way.
// Written with the extension anyway, to match every other import of this
// module in src/lib and so nobody has to work out which form they are
// looking at to know whether it is safe.
import type { AttendanceConcernFlag } from './alternates.ts';
// A runtime import, so it carries the extension for the strip-types tests.
import { parseMissNotes, type ConcernMissNote } from './attendanceConcernExplained.ts';

export const ATTENDANCE_CONCERNS_ENDPOINT = '/.netlify/functions/attendanceConcerns';

/**
 * How long THIS BROWSER reuses a scan before asking the server again.
 *
 * THE NUMBER IS DECLARED TWICE and this is the second copy, only because a
 * browser bundle cannot import a Netlify function:
 * ATTENDANCE_CONCERNS_CACHE_MS in netlify/functions/lib/attendanceConcernsLocal.mjs
 * is the canonical one and governs the SHARED server cache.
 * tests/attendance-concerns-client.test.mjs pins the two equal.
 *
 * WHAT PINNING THEM EQUAL DOES NOT DO, said plainly because an earlier version
 * of this comment claimed otherwise: it does not bound the total age of what
 * is on screen to two minutes. THE TWO WINDOWS ADD. This one is measured from
 * when the FETCH STARTED; the server's is measured from when the SCAN RAN. A
 * browser that fetches at the last moment of the server's window, and then
 * holds that answer for its own full window, shows a stamp nearly four minutes
 * old. The CEILING is the two windows added, 240 seconds, and
 * tests/attendance-concerns-client.test.mjs measures it rather than asserting
 * it: twenty focus re-asks twelve seconds apart, against a server answering
 * with the oldest stamp its own window allows. An earlier version of this
 * comment quoted a measurement no committed test made, which is a word doing
 * work it has not earned.
 *
 * That is ACCEPTED, not overlooked, and the reason is the stamp. The page
 * prints the scan's own time, so a four minute old list says it is four
 * minutes old. Keying both on the same clock would mean the browser asking the
 * server on every render to find out how old its own answer is, which is the
 * per-viewer cost this whole change exists to remove. An honest old number
 * beats an expensive fresh one, and staleness beyond this is what Refresh and
 * the focus re-ask are for.
 */
export const ATTENDANCE_CONCERNS_TTL_MS = 2 * 60 * 1000;

/** One (offering, dancer) pair Kim should check on. Same fields the browser-side scan produced. */
export interface AttendanceConcernPayload extends AttendanceConcernFlag {
  offeringId: string;
  studentId: string;
  /** Most recent date (ISO YYYY-MM-DD) we have any attendance record on file for this pair. */
  lastSeenDate: string;
  /**
   * The teacher's absent note beside each miss that has one (Kim, 2026-09-28).
   * Only the answer travels, never the rest of the staff-only note.
   */
  missNotes: ConcernMissNote[];
  /** Every miss behind this concern carries a teacher's explanation. */
  explained: boolean;
}

export interface AttendanceConcernsScan {
  concerns: AttendanceConcernPayload[];
  /** Epoch millis the server ran the scan at. What the page stamps in studio time. */
  scannedAt: number;
  /** The oldest date (ISO YYYY-MM-DD) the scan looked at, so the window is never a guess. */
  windowStart: string;
}

/** Injected in tests only; the app always uses the real ones. */
export interface AttendanceConcernsDeps {
  fetch?: typeof fetch;
  /** Resolves the Authorization and App Check headers for the signed-in user. */
  headers?: () => Promise<Record<string, string>>;
  now?: () => number;
}

/**
 * Plain words for a failed scan. The server's codes never reach a screen:
 * "staff_only" is not a sentence anyone at the studio should have to read.
 */
export function attendanceConcernsMessage(code: string, status: number): string {
  if (code === 'sign_in_required' || status === 401) {
    return 'Sign in again to scan attendance.';
  }
  if (code === 'staff_only' || status === 403) {
    return 'This account is not allowed to scan attendance.';
  }
  if (code === 'attendance_concerns_busy') {
    return 'Another scan is already running. Try Refresh again in a moment.';
  }
  if (code === 'too_many_requests' || status === 429) {
    return 'That is a lot of scanning. Give it a few minutes and try again.';
  }
  if (status === 503) {
    return 'The Hub could not reach the studio records just now. Try again in a minute.';
  }
  return 'Attendance could not be scanned. Check your connection and try again.';
}

export class AttendanceConcernsError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'AttendanceConcernsError';
    this.status = status;
  }
}

function asConcern(value: unknown): AttendanceConcernPayload | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const rule = row.rule;
  if (rule !== 'comp-total-3' && rule !== 'rec-consecutive-2') return null;
  if (typeof row.offeringId !== 'string' || typeof row.studentId !== 'string') return null;
  if (typeof row.lastSeenDate !== 'string' || typeof row.reason !== 'string') return null;
  if (typeof row.missCount !== 'number') return null;
  const missDates = Array.isArray(row.missDates)
    ? row.missDates.filter((date): date is string => typeof date === 'string')
    : [];
  return {
    offeringId: row.offeringId,
    studentId: row.studentId,
    lastSeenDate: row.lastSeenDate,
    rule,
    reason: row.reason,
    missCount: row.missCount,
    missDates,
    missNotes: parseMissNotes(row.missNotes),
    // Absent means NOT explained: a row with no flag is a call, never an
    // assumed all-clear.
    explained: row.explained === true,
  };
}

async function defaultHeaders(): Promise<Record<string, string>> {
  const [{ getAuth }, { functionHeaders }] = await Promise.all([
    import('firebase/auth'),
    import('./functionSecurity'),
  ]);
  const user = getAuth().currentUser;
  if (!user) throw new AttendanceConcernsError(attendanceConcernsMessage('sign_in_required', 401), 401);
  return functionHeaders(user);
}

/**
 * One call. No cache, no sharing: loadAttendanceConcerns below owns both.
 *
 * `refresh` is passed THROUGH to the server, not just used here, because the
 * server keeps a shared cache of its own: skipping only the browser's copy
 * would hand Refresh a stored answer and the button would lie.
 */
export async function fetchAttendanceConcerns(
  deps: AttendanceConcernsDeps = {},
  options: { force?: boolean } = {}
): Promise<AttendanceConcernsScan> {
  const doFetch = deps.fetch ?? fetch;
  const response = await doFetch(ATTENDANCE_CONCERNS_ENDPOINT, {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: await (deps.headers ?? defaultHeaders)(),
    body: JSON.stringify({ refresh: options.force === true }),
  });
  const payload = await response.json().catch(() => null) as Record<string, unknown> | null;
  if (!response.ok || !payload) {
    const code = typeof payload?.error === 'string' ? payload.error : '';
    throw new AttendanceConcernsError(
      attendanceConcernsMessage(code, response.status),
      response.status
    );
  }
  const rows = Array.isArray(payload.concerns) ? payload.concerns : [];
  const concerns: AttendanceConcernPayload[] = [];
  for (const row of rows) {
    const concern = asConcern(row);
    if (concern) concerns.push(concern);
  }
  return {
    concerns,
    // A server that answered without a stamp gets this device's clock rather
    // than a zero: the line under the header says "as of", and 1970 would be
    // a worse lie than a second's drift.
    scannedAt: typeof payload.scannedAt === 'number' ? payload.scannedAt : (deps.now ?? Date.now)(),
    windowStart: typeof payload.windowStart === 'string' ? payload.windowStart : '',
  };
}

interface CacheEntry {
  promise: Promise<AttendanceConcernsScan>;
  /** When the call STARTED. A slow scan does not get a longer life for being slow. */
  startedAtMs: number;
  /**
   * WHOSE ANSWER THIS IS. The list is studio-wide and identical for every
   * staff viewer, so this is not about what the rows contain; it is about not
   * handing one account's fetch to the next account to sign in on the same
   * tab. lib/familyStaffRolesCache.ts keys and clears for the same reason, and
   * this one was neither until review asked. `null` means the caller did not
   * name a uid, which only happens in tests.
   */
  uid: string | null;
}

let cached: CacheEntry | null = null;

/**
 * The scan, shared. `force` is Refresh: it drops whatever is held, tells the
 * SERVER to skip its shared copy too, and every consumer mounted at that
 * moment gets the new answer.
 *
 * A FAILED SCAN IS NEVER CACHED. The entry is cleared before the rejection
 * travels, so the next mount, or the next tap of Refresh, gets to try again
 * rather than being told the same stale failure for two minutes. The clear is
 * guarded on the entry still being the current one, so a slow failure cannot
 * throw away a newer good answer that landed while it was in flight.
 */
export function loadAttendanceConcerns(
  options: { force?: boolean; uid?: string | null } = {},
  deps: AttendanceConcernsDeps = {}
): Promise<AttendanceConcernsScan> {
  const nowMs = (deps.now ?? Date.now)();
  const uid = options.uid ?? null;
  const fresh = cached
    && cached.uid === uid
    && nowMs - cached.startedAtMs < ATTENDANCE_CONCERNS_TTL_MS;
  if (!options.force && fresh) return cached!.promise;
  const entry: CacheEntry = {
    startedAtMs: nowMs,
    uid,
    promise: fetchAttendanceConcerns(deps, { force: options.force === true })
      .catch((error: unknown) => {
        if (cached === entry) cached = null;
        throw error;
      }),
  };
  cached = entry;
  return entry.promise;
}

/**
 * Is the answer this browser is holding older than the freshness window?
 *
 * The hook asks this on mount and when the tab regains focus, so a Command
 * Center left open all evening re-asks instead of going on showing a count
 * from an hour ago. A fresh entry makes this false and nothing happens; a
 * stale one costs at most one call, which the SERVER usually answers out of
 * its own shared cache without touching the attendance window at all.
 */
export function attendanceConcernsAreStale(
  scannedAt: number | null,
  nowMs: number = Date.now()
): boolean {
  if (scannedAt === null) return false;
  return nowMs - scannedAt >= ATTENDANCE_CONCERNS_TTL_MS;
}

/**
 * Forget everything this browser is holding.
 *
 * Called from lib/auth.ts signOut(), beside the other per-account caches. No
 * live leak was found (today's sign-out ends in a reload), but a cache that
 * survives a sign-out is a bug waiting for the first flow that does not
 * reload, and the fix is one line.
 */
export function clearAttendanceConcernsCache(): void {
  cached = null;
}

/** Test-only escape hatch: clears the module-level cache between cases. */
export function __resetAttendanceConcernsCacheForTests(): void {
  cached = null;
}
